import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import {
  Link, Outlet, useLocation, useOutletContext,
} from 'react-router-dom';
import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useAppContext } from '../../context/AppContext';
import PermissionsResponse from '../../interfaces/api/Permissions/PermissionsResponse';
import FormattedMessageRollover from '../../lang/FormattedMessage';
import SummerEnum from '../../enum/Summer';
import { getCurrentSchoolYear } from '../../util/shared';

const StyledLink = styled(Link)`
  text-decoration: none;
  color: #506077;
  &:focus {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
  @media (max-width: 1322px) {
    
  }
`
const NavButton = styled(Button) <{ border: string }>`
 border: ${(props) => (props.border)};
 &:focus {
  outline: 2px solid currentColor;
  outline-offset: 1px;
}
`
const StyledMain = styled.main`
  margin-top: 5rem;
  width: 100%;
  @media (max-width: 320px) {
    margin-top: 9rem;
  }
`;

type PermissionsContext = { permissionsData: PermissionsResponse | undefined, isPermissionsLoading: boolean, isPermissionsLoadingError: boolean };
const navItems = [
  { name: 'home', route: '/levelset/scheduler', pathMatch: 'scheduler' },
  { name: 'permissions', route: '/levelset/scheduler/permissions', pathMatch: 'permissions' },
];

export default function DrawerAppBar() {
  const {
    districtId, user: { schoolId, programId, summerAccess }, httpClient: { getPermissions }, setInterimIITestDisabled, setIsSummer, schoolYearEnd,
  } = useAppContext();

  const {
    data: permissionsData, isLoading: isPermissionsLoading, isError: isPermissionsLoadingError,
  } = getPermissions({
    districtId, schoolId: schoolId ?? 0, programId, isEnabled: !!districtId,
  });

  useEffect(() => {
    if (permissionsData !== undefined) {
      setInterimIITestDisabled(!permissionsData.interim_II_test_enabled.value);
      setIsSummer(
        (getCurrentSchoolYear() >= schoolYearEnd
          && summerAccess
          && permissionsData?.levelset_scheduler_include_summer?.value) ? SummerEnum.Yes : SummerEnum.No,
      )
    }
  }, [permissionsData])
  const permissionsContextVal: PermissionsContext = {
    permissionsData,
    isPermissionsLoading,
    isPermissionsLoadingError,
  }

  const { pathname } = useLocation();
  function getBorder(item: { name: string, route: string, pathMatch: string }) {
    return [...pathname.split('/')].pop() === item.pathMatch ? '2px solid grey' : 'none'
  }
  const getTopNavMargins = () => {
    if (window.location.hostname === 'localhost') {
      return '0';
    }
    return Number(programId) === 1 ? '4.25rem' : '7.25rem';
  }
  const a11YProps = (route: string): {disableRipple: boolean, 'aria-current': 'page' | false} => ({
    disableRipple: true,
    'aria-current': route === pathname ? 'page' : false,
  })

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        component="nav"
        sx={{
          top: getTopNavMargins(), position: 'absolute', backgroundColor: '#fff', zIndex: 0,
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            component="h1"
            sx={{ flexGrow: 1, display: { xs: 'block', sm: 'block' } }}
          >
            <StyledLink to="/levelset/scheduler">
              <FormattedMessageRollover id="levelSetScheduler" />
            </StyledLink>
          </Typography>
          <Box sx={{ display: { xs: 'block', sm: 'block' } }}>
            {navItems.map((item) => (
              <NavButton {...a11YProps(item.route)} href={item.route} key={item.name} border={getBorder(item)}>
                <Typography component="span" sx={{ 'text-decoration': 'none', color: '#506077' }}>
                  <FormattedMessageRollover id={item.name} defaultMessage={item.name} />
                  {' '}
                </Typography>
              </NavButton>
            ))}
          </Box>
        </Toolbar>
      </AppBar>
      <StyledMain>
        <Outlet context={permissionsContextVal} />
      </StyledMain>
    </Box>
  );
}

export function usePermissions() {
  return useOutletContext<PermissionsContext>();
}
